// TelcoTrack - Map search query normalization / token matching
// Used by BE address search and web unified search so address order / punctuation
// variants match (e.g. "Stachovice, č.p. 1" ≈ "1, Stachovice").
// Also strips parcel / k.ú. labels and folds diacritics for client-side matching.

/**
 * Czech address / parcel / cadastral labels and common punctuation noise.
 * Note: JS `\b` is ASCII-only (`\w` = [A-Za-z0-9_]), so `č`/`ú` are not word chars —
 * do not anchor those labels with `\b`.
 *
 * Longer phrases first (číslo popisné before č.p., číslo evidenční before č.ev.,
 * katastrální území before k.ú.). „č.ev.“ must also precede „č.p.“-style single-letter
 * alternatives so "č.ev. 16" does not leave a stray "ev".
 */
const ADDRESS_NOISE_RE =
  /(?:číslo\s+popisné|cislo\s+popisne|číslo\s+evidenční|cislo\s+evidencni|katastrální\s+území|katastralni\s+uzemi|parc\.?\s*č\.?|p\.?\s*č\.?|č\.?\s*ev\.?|c\.?\s*ev\.?|č\.?\s*e\.(?=\s*\d)|c\.?\s*e\.(?=\s*\d)|č\.?\s*p\.?|c\.?\s*p\.?|k\.\s*ú\.?|k\.\s*u\.?|(?:^|[\s,;|])parcel[ay]?(?=[\s,;.|]|$)|(?:^|[\s,;|])(?:cp|ce|pč)\.?(?=[\s,;.|]|$)|(?:^|[\s,;|])st\.?\s*(?=\d))/gi;

/** Commas etc. → spaces; keep `/` so č.p. 12/3 and parcela 123/4 stay one token. */
const PUNCT_TO_SPACE_RE = /[,;|·•]+/g;

/** Collapse spaces around `/` so "12 / 3" and "1020/ 7" normalize to one token. */
const SLASH_SPACE_RE = /\s*\/\s*/g;

/**
 * Strip Czech address/parcel labels and punctuation; collapse whitespace.
 * Preserves diacritics and letter case (callers lower/fold as needed).
 */
export function stripMapSearchNoise(query: string): string {
  return (query ?? '')
    .replace(/\u00A0/g, ' ')
    .replace(ADDRESS_NOISE_RE, ' ')
    .replace(PUNCT_TO_SPACE_RE, ' ')
    .replace(SLASH_SPACE_RE, '/')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Lowercase + strip combining marks (č→c, ú→u) for tolerant client matching. */
export function foldMapSearchDiacritics(text: string): string {
  return (text ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();
}

/**
 * Lowercase, fold diacritics, strip č.p./parcela/k.ú. noise and commas.
 * Used for client-side token AND matching (web + KMP parity).
 */
export function normalizeMapSearchQuery(query: string): string {
  return foldMapSearchDiacritics(stripMapSearchNoise(query));
}

/**
 * Noise-stripped tokens with diacritics kept (lowercased).
 * Prefer for DB ILIKE / Nominatim so Czech place names still hit stored accents.
 */
export function tokenizeMapSearchQueryPreservingDiacritics(query: string): string[] {
  const cleaned = stripMapSearchNoise(query).toLowerCase().replace(/\s+/g, ' ').trim();
  if (!cleaned) return [];
  return cleaned.split(' ').filter((t) => t.length > 0);
}

/** Significant tokens for AND-matching across address fields (diacritics folded). */
export function tokenizeMapSearchQuery(query: string): string[] {
  const normalized = normalizeMapSearchQuery(query);
  if (!normalized) return [];
  return normalized.split(' ').filter((t) => t.length > 0);
}

/**
 * Join tokens for external geocoders (Nominatim) — order-neutral cleaned string.
 * Keeps diacritics (Nominatim ranks accented Czech names better).
 */
export function cleanedMapSearchQueryForExternal(query: string): string {
  return tokenizeMapSearchQueryPreservingDiacritics(query).join(' ');
}

/** Concatenate searchable fields into one haystack (folded for matching). */
export function mapSearchHaystack(...parts: Array<string | number | null | undefined>): string {
  return foldMapSearchDiacritics(
    parts
      .map((p) => (p == null ? '' : String(p)))
      .filter((s) => s.length > 0)
      .join(' '),
  );
}

/**
 * House-number / č.p. / parcel-number tokens: digits, optional slash segment (12/3, 1020/7).
 * These must match a whole haystack field-token so "7" hits č.p. 7,
 * not object ids (`…7c`) or parcels (`1020/7`).
 */
export function isHouseNumberMapSearchToken(token: string): boolean {
  return /^\d+(?:\/\d+)?$/.test(token);
}

/** True when every token appears somewhere in haystack (order-independent). */
export function matchesMapSearchTokens(haystack: string, tokens: string[]): boolean {
  if (tokens.length === 0) return false;
  const h = foldMapSearchDiacritics(haystack);
  const hayTokens = h.split(/\s+/).filter(Boolean);
  return tokens.every((t) => {
    const token = foldMapSearchDiacritics(t);
    if (!token) return false;
    if (isHouseNumberMapSearchToken(token)) {
      if (hayTokens.some((ht) => ht === token)) return true;
      // č.p. 23 + orientační 5 stored as separate fields — query "23/5" still matches.
      const slash = token.indexOf('/');
      if (slash >= 0) {
        const cp = token.slice(0, slash);
        const orient = token.slice(slash + 1);
        return hayTokens.includes(cp) && hayTokens.includes(orient);
      }
      return false;
    }
    return h.includes(token);
  });
}

/**
 * Match query against one or more field strings.
 * Falls back to substring on normalized query when tokenization yields nothing.
 */
export function matchesMapSearchQuery(
  query: string,
  ...fields: Array<string | number | null | undefined>
): boolean {
  const tokens = tokenizeMapSearchQuery(query);
  const haystack = mapSearchHaystack(...fields);
  if (!haystack) return false;
  if (tokens.length === 0) {
    const normalized = normalizeMapSearchQuery(query);
    return normalized.length > 0 && haystack.includes(normalized);
  }
  return matchesMapSearchTokens(haystack, tokens);
}
